import React from 'react';
import * as yup from 'yup';

import Button from '@hero/ui-kit/inputs/Button';
import Form from '@hero/ui-kit/inputs/Form';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';

import { useResolveMissingMinutes } from '../api/useCareWrites';
import { careErrorText } from '../constants';
import * as Style from '../style.module.scss';

const schema = yup.object({ reason: yup.string().trim().required('Enter a reason.') });
const SKIP_LABELS: Record<string, string> = {
    obsolete: 'No longer eligible',
    held: 'On hold',
    excluded: 'Already excluded',
    approved_or_released: 'Approved or released',
    no_missing_minutes_warning: 'No missing-minutes review needed',
    already_resolved: 'Already resolved',
    reported_time: 'Has reported time',
    other_review: 'Other review required',
    action_unavailable: 'Action unavailable'
};

const MissingMinutesForm: React.FC<{
    month: string;
    canWrite: boolean;
    closeModal: () => void;
    onCalculationResult: (error?: string) => void;
    reviewCount?: number;
}> = ({ month, canWrite, closeModal, onCalculationResult, reviewCount }) => {
    const mutation = useResolveMissingMinutes(month, onCalculationResult);
    const report = mutation.data;

    return (
        <Section>
            <H role="h5">Mark missing-minutes codes not billable</H>
            <P>Service month: {month}</P>
            {report ? (
                <>
                    <P role="status">{report.changed_codes} codes marked not billable.</P>
                    {Object.entries(report.skipped_codes_by_reason).map(([reason, count]) => (
                        <P key={reason} noDefaultMargin>{SKIP_LABELS[reason] ?? reason}: {count}</P>
                    ))}
                    <Button onClick={closeModal}>Done</Button>
                </>
            ) : (
                <>
                    <P>
                        Apply one reason to every code in this month whose only pending review is
                        missing minutes from the CSV. Table filters do not limit this action.
                    </P>
                    {reviewCount !== undefined ? (
                        <P>{reviewCount} codes currently show only missing-minutes review. Eligibility
                            is checked again when you confirm.</P>
                    ) : null}
                    <P>
                        Held, excluded, approved or released codes and codes with reported time or
                        other pending reviews are skipped. Each changed code gets its own history entry.
                    </P>
                    <Form
                        validationSchema={schema}
                        validationMode="onTouched"
                        submitFn={async (values: { reason: string }) => {
                            if (!canWrite || mutation.isPending) return;
                            try {
                                await mutation.mutateAsync(values);
                            } catch {
                                // The hook and inline alert present the refusal; the form remains editable.
                            }
                        }}
                    >
                        <TextArea name="reason" displayName="Reason for all changed codes"
                            disabled={!canWrite || mutation.isPending} />
                        {mutation.error ? (
                            <P role="alert" className={Style.apiError}>{careErrorText(mutation.error)}</P>
                        ) : null}
                        <Section noDefaultPadding paddingTop="small" centered>
                            <Button type="submit" disabled={!canWrite || mutation.isPending}>
                                Confirm not billable
                            </Button>
                        </Section>
                    </Form>
                </>
            )}
        </Section>
    );
};

export default MissingMinutesForm;
